import { z } from 'zod'

import { OperationLevelPermission } from '../auth-claims/permissions'
import { regionCollectionSchema } from '../geojson-schemas'
import { defineApi } from '../http'
import { HeadlandCornerType } from '../locutus/guidance'
import { helmFieldFeaturesResponseSchema } from './field-features-response'

/**
 * `properties.group` values helm stamps on the features `/guidance/boundary-inspect`
 * emits.
 */
export enum BoundaryInspectGroup {
  FieldBoundary = 'field_boundary',
  BoundaryRings = 'boundary_rings',
  BoundarySides = 'boundary_sides',
  BoundaryCorners = 'boundary_corners',
  BoundaryHoles = 'boundary_holes',
  BoundaryIntersections = 'boundary_intersections',
  BoundaryCorridors = 'boundary_corridors'
}

/**
 * A detected corridor: two facing region edges separated by a strip that
 * belongs to neither region — a waterway, grassed strip or lane splitting one
 * physical field into two boundary polygons. The feature's geometry is the
 * closed strip polygon between the banks.
 */
export const boundaryCorridorPropertiesSchema = z.object({
  id: z.string(),
  group: z.literal(BoundaryInspectGroup.BoundaryCorridors),
  /** The two boundary feature ids the corridor joins, `[A, B]`. */
  regions: z.tuple([z.string(), z.string()]),
  /** Side ids bank A spans, in ring order; a curved bank may span several. */
  edgesA: z.array(z.string()).min(1),
  /** Side ids bank B spans, in ring order. */
  edgesB: z.array(z.string()).min(1),
  /** Median gap between the banks, metres. */
  widthM: z.number().nonnegative(),
  /** Arc length of the longer bank, metres. */
  lengthM: z.number().nonnegative()
})

export type BoundaryCorridorProperties = z.infer<typeof boundaryCorridorPropertiesSchema>

/**
 * Derives the boundary geometry (rings, holes, detected sides, and corridors)
 * for a field boundary via Helm's guidance service. Unlike guidance path
 * generation, this reports boundary structure only; it does not propagate
 * guidance lines.
 *
 * The `boundary` must be a feature collection — Helm parses it as one — so the
 * single-feature `regionSchema` form of a boundary is not accepted here.
 */
export const boundaryInspectApi = defineApi({
  method: 'POST',
  pathFor: () => '/guidance/boundary-inspect',
  name: 'InspectFieldBoundary',
  description:
    'Derive the boundary geometry (rings, holes, detected sides, and corridors between regions) for a field boundary, optionally specifying how headland corners are treated.',
  requiredPermissions: OperationLevelPermission.ManagePlans,
  requestBodySchema: z.object({
    boundary: regionCollectionSchema,
    cornerType: z.enum(HeadlandCornerType).optional()
  }),
  responseSchema: helmFieldFeaturesResponseSchema
})
